// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Entry-name safety for the exported ZIP.
 *
 * This is a security control, not tidiness. Loraprep accepts a ZIP as input, and a hostile archive
 * can carry entries like `../../.ssh/authorized_keys` or `C:\Windows\System32\x`. If those names
 * were copied through into the export, extracting Loraprep's output would write outside the target
 * directory — the Zip Slip class of bug, still live in plenty of extractors. Every name that goes
 * into the output archive passes through here first.
 *
 * The same function also handles the ordinary case: turning a user's filename into something that
 * survives being extracted on Windows, macOS and Linux alike.
 */

/** Windows refuses these as a base name regardless of extension, with or without one. */
const RESERVED = new Set([
  'con', 'prn', 'aux', 'nul',
  'com1', 'com2', 'com3', 'com4', 'com5', 'com6', 'com7', 'com8', 'com9',
  'lpt1', 'lpt2', 'lpt3', 'lpt4', 'lpt5', 'lpt6', 'lpt7', 'lpt8', 'lpt9',
]);

export function splitExtension(name: string): { stem: string; ext: string } {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return { stem: name, ext: '' };
  const ext = name.slice(dot + 1).toLowerCase();
  // Only treat it as an extension if it looks like one. `IMG_2024.03.11 dinner` must not lose its tail.
  if (!/^[a-z0-9]{1,8}$/.test(ext)) return { stem: name, ext: '' };
  return { stem: name.slice(0, dot), ext };
}

/**
 * One path segment, made safe.
 *
 * Strips every directory separator (so a segment can never become two), the Windows-forbidden
 * characters, control bytes, and leading dots — which is what stops `..` surviving as a segment at
 * all. Trailing dots and spaces go too, because Windows silently drops them and two names that
 * differ only there would then collide on extraction.
 */
export function safeSegment(raw: string): string {
  let s = raw
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/[/\\:*?"<>|]/g, '_')
    // Collapse every run of dots to one. Stripping only the LEADING dots is not enough: once the
    // separators above become underscores, `../../etc/passwd` still carries `..` in the middle.
    // An interior `..` inside a single segment cannot traverse anything, but "the output contains
    // no `..` at all" is a property a reviewer can check at a glance, and that is worth more here
    // than preserving somebody's `photo..jpg`.
    .replace(/\.{2,}/g, '.')
    .replace(/^\.+/, '')
    .replace(/[. ]+$/, '')
    .trim();
  if (s.length > 96) {
    const { stem, ext } = splitExtension(s);
    s = ext ? `${stem.slice(0, 96 - ext.length - 1)}.${ext}` : s.slice(0, 96);
  }
  const { stem, ext } = splitExtension(s);
  if (RESERVED.has(stem.toLowerCase())) s = ext ? `_${stem}.${ext}` : `_${stem}`;
  return s.length > 0 ? s : 'image';
}

/**
 * The kohya_ss concept directory: `<repeats>_<concept>`.
 *
 * The number before the underscore is how many times sd-scripts repeats each image per epoch, and
 * the word after it is the class/concept token. sd-scripts parses this name, so a stray space or
 * separator here does not produce a warning — it produces a silently skipped folder and a training
 * run on nothing.
 */
export function conceptDirName(repeats: number, concept: string): string {
  const n = Math.max(1, Math.min(9999, Math.floor(repeats) || 1));
  // NOT via safeSegment: its own "never return empty" fallback is the word `image`, which would
  // leak into the class token and train the concept on the wrong word. A concept that sanitises
  // away needs its own fallback, applied here.
  const c = concept
    .replace(/[^A-Za-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48)
    .replace(/_+$/, '');
  return `${n}_${c.length > 0 ? c : 'concept'}`;
}

/**
 * Assign each kept image a unique, safe name inside the concept directory.
 *
 * Original basenames are kept wherever possible — they are how the user recognises their own
 * photographs, and the full original path is recorded in `metadata.jsonl` and `dedup-report.csv`
 * either way. Collisions (two folders each holding `IMG_0001.JPG`, which is the normal case when
 * someone merges phone exports) get a numeric suffix rather than one overwriting the other.
 */
export function assignExportNames(paths: readonly string[]): string[] {
  const used = new Set<string>();
  const out: string[] = [];
  for (const p of paths) {
    const base = safeSegment(p.split('/').pop() ?? p);
    const { stem, ext } = splitExtension(base);
    // `splitExtension` lowercases the extension for matching; the name keeps the user's own
    // casing, so `IMG_0001.JPG` collides into `IMG_0001_2.JPG` rather than silently becoming
    // lowercase halfway down a folder.
    const suffix = ext ? base.slice(stem.length) : '';
    let candidate = base;
    let n = 2;
    while (used.has(candidate.toLowerCase())) {
      candidate = `${stem}_${n}${suffix}`;
      n++;
    }
    used.add(candidate.toLowerCase());
    out.push(candidate);
  }
  return out;
}
